import React, { useState, useRef, useCallback } from 'react';
import { documentsAPI } from '../../api/client';
import {
  CloudArrowUpIcon,
  XMarkIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import FileIcon from './FileIcon';
import FilePreviewModal from './FilePreviewModal';

const ALLOWED_TYPES = ['.pdf', '.docx', '.doc', '.xlsx', '.xls', '.txt'];
const FILE_ICONS = { pdf: '📄', docx: '📝', doc: '📝', xlsx: '📊', xls: '📊', txt: '📃' };

let nextId = 0;

function FileRow({ item, onRemove }) {
  const ext = item.file.name.split('.').pop().toLowerCase();
  const icon = FILE_ICONS[ext] || '📎';

  return (
    <div className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg">
      <span className="text-lg flex-shrink-0">{icon}</span>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 truncate">{item.file.name}</p>

        {item.status === 'uploading' && (
          <div className="mt-1">
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full transition-all duration-150"
                style={{ width: `${item.progress}%` }}
              />
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{item.progress}%</p>
          </div>
        )}
        {item.status === 'success' && (
          <p className="text-xs text-green-600 mt-0.5">Uploaded — indexing in background…</p>
        )}
        {item.status === 'error' && (
          <p className="text-xs text-red-500 mt-0.5 truncate">{item.error}</p>
        )}
      </div>

      <div className="flex-shrink-0">
        {item.status === 'pending' && (
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(item.id); }}
            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}
        {item.status === 'uploading' && (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
        )}
        {item.status === 'success' && (
          <CheckCircleIcon className="w-5 h-5 text-green-500" />
        )}
        {item.status === 'error' && (
          <ExclamationCircleIcon className="w-5 h-5 text-red-500" />
        )}
      </div>
    </div>
  );
}

export default function DocumentUpload({ projectId, onUploaded }) {
  const [dragging, setDragging] = useState(false);
  const [fileItems, setFileItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const addFiles = useCallback((fileList) => {
    const newItems = [];
    for (const file of fileList) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_TYPES.includes(ext)) {
        toast.error(`"${file.name}": unsupported type`);
        continue;
      }
      newItems.push({ id: nextId++, file, status: 'pending', progress: 0, error: null });
    }
    if (newItems.length) setFileItems((prev) => [...prev, ...newItems]);
  }, []);

  const removeFile = useCallback((id) => {
    setFileItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const uploadAll = useCallback(async () => {
    const pending = fileItems.filter((f) => f.status === 'pending');
    if (!pending.length) return;

    setUploading(true);
    setFileItems((prev) =>
      prev.map((item) =>
        item.status === 'pending' ? { ...item, status: 'uploading' } : item
      )
    );

    await Promise.allSettled(
      pending.map(async (item) => {
        try {
          const res = await documentsAPI.upload(projectId, item.file, (pct) => {
            setFileItems((prev) =>
              prev.map((f) => (f.id === item.id ? { ...f, progress: pct } : f))
            );
          });
          setFileItems((prev) =>
            prev.map((f) =>
              f.id === item.id ? { ...f, status: 'success', progress: 100 } : f
            )
          );
          onUploaded(res.data);
        } catch (err) {
          const msg = err.response?.data?.detail || 'Upload failed';
          setFileItems((prev) =>
            prev.map((f) => (f.id === item.id ? { ...f, status: 'error', error: msg } : f))
          );
        }
      })
    );

    setUploading(false);
  }, [fileItems, projectId, onUploaded]);

  const clearCompleted = () =>
    setFileItems((prev) => prev.filter((f) => f.status !== 'success'));

  const pendingCount = fileItems.filter((f) => f.status === 'pending').length;
  const uploadingCount = fileItems.filter((f) => f.status === 'uploading').length;
  const successCount = fileItems.filter((f) => f.status === 'success').length;

  return (
    <div className="space-y-3">
      {/* Drop zone — compact horizontal layout */}
      <div
        className={`border-2 border-dashed rounded-lg px-4 py-2.5 flex items-center gap-3 transition-colors cursor-pointer ${
          dragging ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
        onClick={() => fileRef.current?.click()}
      >
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          multiple
          accept={ALLOWED_TYPES.join(',')}
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
        />
        <CloudArrowUpIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-gray-600 leading-tight">
            Drop files here or click to browse
          </p>
          <p className="text-xs text-gray-400">PDF, Word, Excel, TXT · Max 50MB · Multiple files</p>
        </div>
      </div>

      {/* Upload queue */}
      {fileItems.length > 0 && (
        <div className="space-y-1.5">
          <div className="max-h-40 overflow-y-auto space-y-1.5 pr-0.5">
            {fileItems.map((item) => (
              <FileRow key={item.id} item={item} onRemove={removeFile} />
            ))}
          </div>

          <div className="flex items-center justify-between pt-0.5">
            {successCount > 0 && (
              <button
                onClick={clearCompleted}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                Clear uploaded ({successCount})
              </button>
            )}
            {pendingCount > 0 && (
              <button
                onClick={uploadAll}
                disabled={uploading}
                className="ml-auto px-4 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {uploadingCount > 0
                  ? `Uploading… (${uploadingCount} remaining)`
                  : `Upload ${pendingCount} file${pendingCount !== 1 ? 's' : ''}`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Shown when a document has no chunks but no recorded reason — i.e. it was
// processed before extraction_error existed. Never leave the badge without a
// tooltip: an unexplained red marker is worse than the green tick it replaces.
const NOT_SEARCHABLE_FALLBACK =
  'No text could be extracted, so this document cannot be found by search or used ' +
  'by the AI. Usually a scanned or photographed PDF, or a .docx whose content sits ' +
  'inside images or text boxes. Use re-index to retry.';

export function DocumentList({ documents, onDelete, onReindex }) {
  const [previewDoc, setPreviewDoc] = useState(null);

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const handleDownload = async (e, doc) => {
    e.stopPropagation();
    try {
      const res = await documentsAPI.downloadBlob(doc.id);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  if (!documents.length) {
    return <p className="text-sm text-gray-400 text-center py-4">No documents uploaded yet</p>;
  }

  return (
    <>
      <div className="space-y-2">
        {documents.map((doc) => (
          <div key={doc.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <FileIcon ext={doc.file_type} />

            <div className="flex-1 min-w-0">
              <button
                onClick={() => setPreviewDoc(doc)}
                className="text-sm font-medium text-gray-800 hover:text-primary-600 truncate block w-full text-left transition-colors"
                title="Click to preview"
              >
                {doc.original_filename}
              </button>
              <div className="flex items-center gap-2 mt-0.5">
                {doc.file_size > 0 && (
                  <>
                    <span className="text-xs text-gray-400">{formatSize(doc.file_size)}</span>
                    <span className="text-gray-300">·</span>
                  </>
                )}
                {!doc.processed ? (
                  <span className="text-xs text-yellow-600 font-medium flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
                    Processing…
                  </span>
                ) : doc.chunk_count > 0 ? (
                  <span className="text-xs text-green-600 font-medium">
                    ✓ Indexed · {doc.chunk_count} chunk{doc.chunk_count !== 1 ? 's' : ''}
                  </span>
                ) : (
                  <span
                    className="text-xs text-red-600 font-medium flex items-center gap-1"
                    title={doc.extraction_error || NOT_SEARCHABLE_FALLBACK}
                  >
                    <ExclamationCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    Not searchable · 0 chunks
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={(e) => handleDownload(e, doc)}
                title="Download"
                className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors rounded"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onReindex(doc.id); }}
                title="Re-extract and re-index this document"
                className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors rounded"
              >
                <ArrowPathIcon className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(doc.id); }}
                title="Delete document"
                className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {previewDoc && (
        <FilePreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />
      )}
    </>
  );
}
